import { useState } from "react";
import { Plus, Printer, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProductionLocations, ProductionLocation } from "@/hooks/useProductionLocations";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { supabase } from "@/integrations/supabase/client";
import { ProductionLocationFormDialog } from "@/components/production-locations/ProductionLocationFormDialog";
import { ProductionLocationList } from "@/components/production-locations/ProductionLocationList";
import { PrinterTestCard } from "@/components/production-locations/PrinterTestCard";

export default function LocaisProducaoPage() {
  const {
    locations,
    isLoading,
    createLocation,
    updateLocation,
    deleteLocation,
    toggleActive,
  } = useProductionLocations();

  const { activeRestaurant } = useRestaurantContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<ProductionLocation | null>(null);

  const handleCreate = () => {
    setSelectedLocation(null);
    setDialogOpen(true);
  };

  const handleEdit = (location: ProductionLocation) => {
    setSelectedLocation(location);
    setDialogOpen(true);
  };

  const handleDuplicate = async (location: ProductionLocation) => {
    await createLocation({
      name: `${location.name} (Cópia)`,
      printer_name: location.printer_name,
      is_active: location.is_active,
      job_type: location.job_type,
    });
  };

  const handleDelete = async (id: string) => {
    await deleteLocation(id);
  };

  const handleToggleActive = async (id: string) => {
    await toggleActive(id);
  };

  const handleSubmit = async (data: any) => {
    let locationId: string | null = null;
    if (selectedLocation) {
      await updateLocation(selectedLocation.id, data);
      locationId = selectedLocation.id;
    } else {
      const created = await createLocation(data);
      locationId = created?.id || null;
    }

    // Upsert SLA config
    if (locationId && activeRestaurant) {
      await supabase.from("kds_station_config").upsert(
        {
          restaurant_id: activeRestaurant.id,
          production_location_id: locationId,
          sla_warning_seconds: (data.sla_warning_minutes || 10) * 60,
          sla_critical_seconds: (data.sla_critical_minutes || 20) * 60,
        },
        { onConflict: "restaurant_id,production_location_id" }
      );
    }

    setDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Locais de Produção</h1>
          <p className="text-muted-foreground">
            Configure os locais de produção e suas impressoras
          </p>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Novo Local
        </Button>
      </div>

      {/* Info Card */}
      <div className="rounded-lg border bg-blue-50 dark:bg-blue-900/20 p-4">
        <div className="flex gap-3">
          <Printer className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Configuração de Impressoras
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Utilize o QZ Tray para impressão direta. O nome da impressora deve corresponder 
              exatamente ao nome configurado no Windows (Painel de Controle → Dispositivos e Impressoras).
            </p>
          </div>
        </div>
      </div>

      {/* Printer Test */}
      <PrinterTestCard />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total de Locais</p>
              <p className="text-lg font-semibold">{locations.length}</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Printer className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Locais Ativos</p>
              <p className="text-lg font-semibold">
                {locations.filter((l) => l.is_active).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Locations List */}
      <ProductionLocationList
        locations={locations}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
        onDuplicate={handleDuplicate}
      />

      {/* Form Dialog */}
      <ProductionLocationFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        location={selectedLocation}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
